import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import AnimalCard from '../components/AnimalCard';
import EventCard from '../components/EventCard';
import { animaux, evenements, zones } from '../data/zooData';

const HomePage: React.FC = () => {
  const featuredAnimals = animaux.slice(0, 3);
  const featuredEvents = evenements.slice(0, 2);
  const statsRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Animation des chiffres au scroll
    const options = {
      threshold: 0.2
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counters = entry.target.querySelectorAll('.counter');
          counters.forEach(counter => {
            const target = parseInt(counter.getAttribute('data-target') || '0');
            const duration = 2000; // ms
            const increment = target / (duration / 16);
            let currentCount = 0;
            
            const updateCount = () => {
              currentCount += increment;
              if (currentCount < target) {
                (counter as HTMLElement).innerText = Math.ceil(currentCount).toString();
                requestAnimationFrame(updateCount);
              } else {
                (counter as HTMLElement).innerText = target.toString();
              }
            };
            
            requestAnimationFrame(updateCount);
          });
        }
      });
    }, options);
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  return (
    <>
      <Hero 
        title="Zoo Aventure" 
        subtitle="Découvrez une expérience immersive au cœur de la nature et de la vie sauvage" 
        backgroundImage="https://images.unsplash.com/photo-1503919275948-1f118d8ecf0b?w=1920&auto=format&fit=crop"
        buttonText="Découvrir nos animaux"
        buttonLink="/animaux"
      />
      
      {/* Section Découvrir */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-900 mb-4">Découvrez notre zoo</h2>
            <p className="text-lg text-gray-600">
              Bienvenue au Zoo Aventure, où la préservation de la faune rencontre l'émerveillement.
              Explorez nos différentes zones et rencontrez nos pensionnaires exceptionnels.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {zones.map(zone => (
              <div key={zone.id} className="bg-white rounded-lg overflow-hidden shadow-lg transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                <div 
                  className="h-56 relative overflow-hidden"
                  style={{ backgroundImage: `url(${zone.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-green-800">{zone.nom}</h3>
                  <p className="text-gray-600 mb-4">{zone.description}</p>
                  <Link 
                    to="/animaux" 
                    className="inline-block text-yellow-600 font-medium hover:text-yellow-700 transition-colors"
                  >
                    Explorer cette zone →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Section Animaux en vedette */}
      <section className="py-16 bg-green-900 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Nos animaux vedettes</h2>
              <p className="text-green-200">Venez les rencontrer lors de votre visite</p>
            </div>
            <Link 
              to="/animaux" 
              className="mt-6 md:mt-0 inline-block bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-md transition-colors duration-300"
            >
              Voir tous nos animaux
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredAnimals.map(animal => (
              <AnimalCard key={animal.id} animal={animal} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Section Chiffres clés */}
      <section className="py-16 bg-gray-100" ref={statsRef}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-green-900 mb-12">Le Zoo Aventure en chiffres</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">
                <span className="counter" data-target="150">0</span>+
              </div>
              <p className="text-gray-600 text-lg">Espèces différentes</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">
                <span className="counter" data-target="500">0</span>k
              </div>
              <p className="text-gray-600 text-lg">Visiteurs annuels</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">
                <span className="counter" data-target="25">0</span>
              </div>
              <p className="text-gray-600 text-lg">Hectares de parc</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">
                <span className="counter" data-target="15">0</span>
              </div>
              <p className="text-gray-600 text-lg">Programmes de conservation</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Section Événements */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-green-900 mb-2">Événements à venir</h2>
              <p className="text-gray-600">Ne manquez pas nos activités spéciales</p>
            </div>
            <Link 
              to="/evenements" 
              className="mt-6 md:mt-0 inline-block bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md transition-colors duration-300"
            >
              Voir tous les événements
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredEvents.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Section CTA */}
      <section className="py-20 bg-green-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Prêt pour l'aventure ?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Réservez vos billets dès maintenant et préparez-vous à une journée inoubliable au Zoo Aventure !
          </p>
          <Link 
            to="/billets" 
            className="inline-block bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105"
          >
            Réserver mes billets
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
